import {
    useCreateAgencyAct,
    useGetAgencyCreatives,
    useUpdateAgencyAct,
} from '@/shared/api'
import { QuestionSVGIcon } from '@/shared/assets'
import {
    Button,
    Checkbox,
    DatePickerWithIcon,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
} from '@/shared/ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    VkOrdAgencyContractResponse,
    WorkspaceDescriptorDto,
} from '@synapex/shared2/gateway-rest-api'
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import z from 'zod'
import { AgencyCreativePanel } from './AgencyCreativePanel'
import { toUTCDate } from '@/shared/lib/date'

function getActSuccessMessage(
    action: 'created' | 'updated',
    date: Date
): string {
    const today = toUTCDate(new Date())
    const actDate = toUTCDate(new Date(date))

    const base =
        action === 'created' ? 'Акт успешно создан' : 'Акт успешно обновлен'

    if (actDate > today) {
        const formatted = date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
        })
        return `${base}. Будет передан в ОРД ${formatted}`
    }

    return base
}

const agencyActSchema = z
    .object({
        number: z.string().min(0, 'Номер договора обязателен').optional(),
        isNumberSameAsContract: z.boolean().default(false).optional(),
        startDate: z
            .date({
                error: 'Дата начала обязательна',
            })
            .optional(),
        isStartDateSameAsContract: z.boolean().default(false).optional(),
        endDate: z.date({
            error: 'Дата окончания обязательна',
        }),
        date: z.date({
            error: 'Дата высталения акта обязательна',
        }),
        budget: z
            .string()
            .min(1, 'Сумма должна быть больше 0')
            .refine(
                (val) => !isNaN(parseFloat(val)),
                'Введите корректное число'
            )
            .refine(
                (val) => parseFloat(val) >= 0,
                'Сумма должна быть больше 0'
            ),
        isBudgetCalculatedAutomatically: z.boolean().default(false).optional(),
        remuneration: z
            .string()
            .refine(
                (val) => !val || !isNaN(parseFloat(val)),
                'Введите корректное число'
            )
            .refine(
                (val) => !val || parseFloat(val) >= 0,
                'Сумма должна быть больше или равна 0'
            )
            .optional(),

        budgetInApplication: z
            .string()
            .refine(
                (val) => !val || !isNaN(parseFloat(val)),
                'Введите корректное число'
            )
            .refine(
                (val) => !val || parseFloat(val) >= 0,
                'Сумма должна быть больше 0'
            )
            .optional(),
    })
    .refine((data) => !(!data.isNumberSameAsContract && !data.number?.length), {
        error: 'Нужно заполнить номер договора',
        path: ['number'],
    })
    .refine(
        (data) =>
            !(
                !data.isStartDateSameAsContract &&
                typeof data.startDate === 'undefined'
            ),
        {
            error: 'Нужно выбрать дату начала',
            path: ['startDate'],
        }
    )
    .refine(
        (data) =>
            !(
                !data.isBudgetCalculatedAutomatically &&
                typeof data.budget === 'undefined'
            ),
        {
            error: 'Нужно выбрать сумму бюджета',
            path: ['budget'],
        }
    )
    .refine(
        (data) =>
            !data.budgetInApplication ||
            parseFloat(data.budgetInApplication) >= parseFloat(data.budget),
        {
            error: 'Бюджет в заявке должен быть больше бюджета на рекламу',
            path: ['budgetInApplication'],
        }
    )
    .refine((data) => data.startDate && data.endDate >= data.startDate, {
        error: 'Дата окончания должна быть больше даты начала',
        path: ['endDate'],
    })

type AgencyActFormData = z.infer<typeof agencyActSchema>

type CreateAgencyActDialogProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    editingAct: VkOrdAgencyContractResponse['agency_acts'][number] | null
    contract: VkOrdAgencyContractResponse
} & WorkspaceDescriptorDto

const CreateAgencyActDialog = ({
    open,
    onOpenChange,
    editingAct,
    contract,
    ...props
}: CreateAgencyActDialogProps) => {
    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        setValue,
        watch,
    } = useForm<AgencyActFormData>({
        resolver: async (data, context, options) => {
            const result = await zodResolver(agencyActSchema)(
                data,
                context,
                options
            )

            // Дополнительная проверка для рамочных договоров
            if (contract.is_framework_contract) {
                if (
                    !data.budgetInApplication ||
                    data.budgetInApplication.trim() === ''
                ) {
                    return {
                        values: {},
                        errors: {
                            ...result.errors,
                            budgetInApplication: {
                                type: 'required',
                                message:
                                    'Бюджет в заявке обязателен для рамочного договора',
                            },
                        },
                    }
                }
            }

            return result
        },
        defaultValues: {
            number: '',
            budget: '',
            budgetInApplication: '',
            remuneration: '',
            startDate: toUTCDate(new Date(contract.start_date)),
            endDate: toUTCDate(new Date()),
            date: toUTCDate(new Date()),
            isNumberSameAsContract: false,
            isStartDateSameAsContract: true,
            isBudgetCalculatedAutomatically: false,
        },
    })

    const createAgencyAct = useCreateAgencyAct(props)
    const updateAgencyAct = useUpdateAgencyAct(props)

    const { data: vkOrdCreatives = [] } = useGetAgencyCreatives(
        contract.vk_ord_agency_contract_id,
        {
            enabled: contract.is_framework_contract,
        }
    )

    const allVkOrdCreatives = useMemo(
        () =>
            vkOrdCreatives.filter((c) => {
                return (
                    c.vk_ord_agency_act_id === null ||
                    c.vk_ord_agency_act_id === editingAct?.vk_ord_agency_act_id
                )
            }),
        [vkOrdCreatives, editingAct]
    )

    const isEditing = !!editingAct
    const isLoading = createAgencyAct.isPending || updateAgencyAct.isPending

    const [
        selectedVkOrdCreativeExternalIds,
        setSelectedVkOrdCreativeExternalIds,
    ] = useState<string[]>(() => {
        if (editingAct?.vk_ord_creatives) {
            return editingAct.vk_ord_creatives.map((c) => c.external_id)
        }
        return []
    })

    const [creativesErrorKey, setCreativesErrorKey] = useState(0)

    useEffect(() => {
        if (editingAct) {
            setValue('number', editingAct.number)
            setValue('budget', editingAct.budget.toString())
            setValue(
                'budgetInApplication',
                editingAct.budget_in_application?.toString() || ''
            )
            setValue('startDate', toUTCDate(new Date(editingAct.start_date)))
            setValue('endDate', toUTCDate(new Date(editingAct.end_date)))
            setValue('date', toUTCDate(new Date(editingAct.date)))
            setValue(
                'isNumberSameAsContract',
                editingAct.is_number_same_as_contract
            )
            setValue(
                'isStartDateSameAsContract',
                editingAct.is_start_date_same_as_contract
            )
            setValue('remuneration', editingAct.remuneration.toString())
            setValue(
                'isBudgetCalculatedAutomatically',
                editingAct.is_budget_calculated_automatically
            )
            setSelectedVkOrdCreativeExternalIds(
                editingAct.vk_ord_creatives.map(
                    (vkOrdCreative) => vkOrdCreative.external_id
                )
            )
        }
    }, [setValue, editingAct, contract.is_framework_contract])

    const onSubmit = async (data: AgencyActFormData) => {
        const budget = parseFloat(data.budget)
        const budgetInApplication = data.budgetInApplication
            ? parseFloat(data.budgetInApplication)
            : undefined
        const remuneration =
            data.remuneration && data.remuneration.trim()
                ? parseFloat(data.remuneration)
                : undefined

        if (
            contract.is_framework_contract &&
            data.isBudgetCalculatedAutomatically &&
            selectedVkOrdCreativeExternalIds.length === 0
        ) {
            setCreativesErrorKey((k) => k + 1)
            return
        }

        try {
            if (isEditing) {
                await updateAgencyAct.mutateAsync({
                    actId: editingAct.vk_ord_agency_act_id,
                    act: {
                        number: data.number,
                        budget: budget,
                        start_date: data.startDate,
                        end_date: data.endDate,
                        agencyContractId: contract.vk_ord_agency_contract_id,
                        isNumberSameAsContract: data.isNumberSameAsContract,
                        isStartDateSameAsContract:
                            data.isStartDateSameAsContract,
                        remuneration: remuneration,
                        creatives: selectedVkOrdCreativeExternalIds,
                        budget_in_application: budgetInApplication,
                        date: data.date,
                        isBudgetCalculatedAutomatically:
                            data.isBudgetCalculatedAutomatically,
                    },
                })
                toast.success(getActSuccessMessage('updated', data.date))
            } else {
                await createAgencyAct.mutateAsync({
                    number: data.number,
                    budget: budget,
                    start_date: data.startDate,
                    end_date: data.endDate,
                    isNumberSameAsContract:
                        data.isNumberSameAsContract || false,
                    isStartDateSameAsContract:
                        data.isStartDateSameAsContract || false,
                    remuneration: remuneration || 0,
                    agencyContractId: contract.vk_ord_agency_contract_id,
                    creatives: selectedVkOrdCreativeExternalIds,
                    budget_in_application: budgetInApplication,
                    date: data.date,
                    isBudgetCalculatedAutomatically:
                        data.isBudgetCalculatedAutomatically || false,
                })
                toast.success(getActSuccessMessage('created', data.date))
            }
            onOpenChange(false)
            reset()
            setSelectedVkOrdCreativeExternalIds([])
        } catch (error) {
            toast.error('Произошла ошибка при сохранении акта')
            console.error(error)
        }
    }

    const handleCancel = () => {
        onOpenChange(false)
        reset()
        setSelectedVkOrdCreativeExternalIds([])
    }

    const handleSelectedChange = (
        selectedIds: string[],
        totalBudget: number
    ) => {
        setSelectedVkOrdCreativeExternalIds(selectedIds)
        setValue('budget', totalBudget.toString())
        if (selectedIds.length > 0) {
            setCreativesErrorKey(0)
        }
    }

    const handleCheckIsBudgetCalculatedAutomatically = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.checked) {
            if (contract.is_framework_contract) {
                const selectedCreatives = allVkOrdCreatives.filter((c) =>
                    selectedVkOrdCreativeExternalIds.includes(c.external_id)
                )
                const totalBudget = selectedCreatives.reduce(
                    (sum, creative) => sum + creative.budget,
                    0
                )
                setValue('budget', totalBudget.toString())
            } else {
                setValue('budget', contract.subContractBudgetSum.toString())
            }
        } else {
            setSelectedVkOrdCreativeExternalIds([])
            setValue('budget', '')
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
            <DialogContent
                className="flex max-h-[95%] max-w-md flex-col bg-white"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? 'Редактировать акт' : 'Добавить акт'}
                    </DialogTitle>
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto">
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="space-y-5"
                    >
                        <div className="flex flex-col gap-2">
                            <Label className="block text-sm font-medium">
                                Введите номер
                            </Label>
                            <Controller
                                name="number"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        placeholder="Введите номер"
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={errors.number?.message}
                                        disabled={watch(
                                            'isNumberSameAsContract'
                                        )}
                                        shouldShowClearButton={
                                            !watch('isNumberSameAsContract')
                                        }
                                    />
                                )}
                            />
                            {!contract.is_framework_contract && (
                                <div className="flex items-center gap-1">
                                    <Controller
                                        name="isNumberSameAsContract"
                                        control={control}
                                        render={({ field }) => (
                                            <Checkbox
                                                checked={field.value}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setValue(
                                                            'number',
                                                            contract.number
                                                        )
                                                    } else {
                                                        setValue('number', '')
                                                    }
                                                    field.onChange(e)
                                                }}
                                            />
                                        )}
                                    />
                                    <Label className="block text-xs">
                                        Совпадает с номером договора
                                    </Label>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label className="block text-sm font-medium">
                                Дата выставления
                            </Label>
                            <Controller
                                name="date"
                                control={control}
                                render={({ field }) => (
                                    <DatePickerWithIcon
                                        selected={field.value}
                                        onChange={(date: Date | undefined) => {
                                            const newDate = toUTCDate(
                                                date || new Date()
                                            )
                                            field.onChange(newDate)
                                            setValue('endDate', newDate)
                                        }}
                                        error={Boolean(errors.date?.message)}
                                        dateFormat="dd.MM.yy"
                                        dropdownMode="scroll"
                                        className="text-xs"
                                        minDate={new Date(contract.start_date)}
                                    />
                                )}
                            />
                            {errors.date && (
                                <p className="mt-1 text-xs text-red-500">
                                    {errors.date.message}
                                </p>
                            )}
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label className="block text-sm font-medium">
                                Дата начала
                            </Label>
                            <Controller
                                name="startDate"
                                control={control}
                                render={({ field }) => (
                                    <DatePickerWithIcon
                                        selected={field.value}
                                        onChange={(date: Date | undefined) => {
                                            field.onChange(
                                                toUTCDate(date || new Date())
                                            )
                                        }}
                                        dateFormat="dd.MM.yy"
                                        dropdownMode="scroll"
                                        className="text-xs"
                                        isDisabled={watch(
                                            'isStartDateSameAsContract'
                                        )}
                                        minDate={new Date(contract.start_date)}
                                        maxDate={watch('date')}
                                    />
                                )}
                            />
                            {errors.startDate && (
                                <p className="mt-1 text-xs text-red-500">
                                    {errors.startDate.message}
                                </p>
                            )}

                            <div className="flex items-center gap-1">
                                <Controller
                                    name="isStartDateSameAsContract"
                                    control={control}
                                    render={({ field }) => (
                                        <Checkbox
                                            checked={field.value}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setValue(
                                                        'startDate',
                                                        toUTCDate(
                                                            new Date(
                                                                contract.start_date
                                                            )
                                                        )
                                                    )
                                                } else {
                                                    setValue(
                                                        'startDate',
                                                        toUTCDate(new Date())
                                                    )
                                                }
                                                field.onChange(e)
                                            }}
                                        />
                                    )}
                                />
                                <Label className="block text-xs">
                                    Совпадает с датой начала договора
                                </Label>
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label className="block text-sm font-medium">
                                Дата окончания
                            </Label>
                            <Controller
                                name="endDate"
                                control={control}
                                render={({ field }) => (
                                    <DatePickerWithIcon
                                        selected={field.value}
                                        onChange={(date: Date | undefined) => {
                                            field.onChange(
                                                toUTCDate(date || new Date())
                                            )
                                        }}
                                        dateFormat="dd.MM.yy"
                                        dropdownMode="scroll"
                                        className="text-xs"
                                        error={Boolean(errors.endDate?.message)}
                                        minDate={
                                            watch('startDate') || new Date()
                                        }
                                        maxDate={
                                            watch('date') > new Date()
                                                ? watch('date')
                                                : new Date()
                                        }
                                    />
                                )}
                            />
                            {errors.endDate && (
                                <p className="mt-1 text-xs text-red-500">
                                    {errors.endDate.message}
                                </p>
                            )}
                        </div>

                        {contract.is_framework_contract && (
                            <div className="flex flex-col gap-2">
                                <Label className="flex items-center gap-1 text-sm font-medium">
                                    <span>Сумма акта</span>
                                    <Tooltip>
                                        <TooltipTrigger type="button">
                                            <QuestionSVGIcon className="cursor-pointer" />
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            <p>Общая cумма акта</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </Label>
                                <Controller
                                    name="budgetInApplication"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="text"
                                            placeholder="Сумма, ₽"
                                            value={field.value}
                                            onChange={(value: string) => {
                                                field.onChange(value)
                                            }}
                                            error={
                                                errors.budgetInApplication
                                                    ?.message
                                            }
                                        />
                                    )}
                                />
                            </div>
                        )}

                        {contract.type !== 'service' && (
                            <div>
                                <Label className="mb-2 flex items-center gap-1 text-sm font-medium">
                                    <span>Вознаграждение посредника</span>
                                    <Tooltip>
                                        <TooltipTrigger type="button">
                                            <QuestionSVGIcon className="cursor-pointer" />
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            <p>Вознаграждение посредника</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </Label>
                                <Controller
                                    name="remuneration"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="text"
                                            placeholder="Сумма, ₽"
                                            value={field.value}
                                            onChange={(value: string) => {
                                                field.onChange(value)
                                            }}
                                            error={errors.remuneration?.message}
                                        />
                                    )}
                                />
                            </div>
                        )}

                        <div className="flex flex-col gap-2">
                            <Label className="flex items-center gap-1 text-sm font-medium">
                                <span>Бюджет на рекламу</span>
                                <Tooltip>
                                    <TooltipTrigger type="button">
                                        <QuestionSVGIcon className="cursor-pointer" />
                                    </TooltipTrigger>
                                    <TooltipContent
                                        avoidCollisions={false}
                                        side="top"
                                    >
                                        <p>Бюджет в разаллокации</p>
                                    </TooltipContent>
                                </Tooltip>
                            </Label>
                            <Controller
                                name="budget"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="text"
                                        placeholder="Сумма, ₽"
                                        value={field.value}
                                        shouldShowClearButton={
                                            // !contract.is_framework_contract &&
                                            !watch(
                                                'isBudgetCalculatedAutomatically'
                                            )
                                        }
                                        disabled={
                                            // contract.is_framework_contract ||
                                            watch(
                                                'isBudgetCalculatedAutomatically'
                                            )
                                        }
                                        onChange={(value: string) => {
                                            field.onChange(value)
                                        }}
                                        error={errors.budget?.message}
                                    />
                                )}
                            />

                            <div className="flex items-center gap-1">
                                <Controller
                                    name="isBudgetCalculatedAutomatically"
                                    control={control}
                                    render={({ field }) => (
                                        <Checkbox
                                            checked={field.value}
                                            onChange={(e) => {
                                                handleCheckIsBudgetCalculatedAutomatically(
                                                    e
                                                )
                                                field.onChange(e)
                                            }}
                                        />
                                    )}
                                />
                                <Label className="block text-xs">
                                    Рассчитать автоматически исходя из выбора
                                    креативов
                                </Label>
                            </div>
                        </div>

                        {contract.is_framework_contract && (
                            <>
                                <div className="flex flex-col gap-2">
                                    <Label className="flex items-center gap-1 text-sm font-medium">
                                        <span>Креативы</span>
                                        <Tooltip>
                                            <TooltipTrigger type="button">
                                                <QuestionSVGIcon className="cursor-pointer" />
                                            </TooltipTrigger>
                                            <TooltipContent side="top">
                                                <p>
                                                    Выберите креативы для акта
                                                </p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </Label>

                                    <div className="flex flex-col gap-2">
                                        <AgencyCreativePanel
                                            vkOrdCreatives={allVkOrdCreatives}
                                            selectedCreativeIds={
                                                selectedVkOrdCreativeExternalIds
                                            }
                                            onSelectionChange={
                                                handleSelectedChange
                                            }
                                            disabled={
                                                !watch(
                                                    'isBudgetCalculatedAutomatically'
                                                )
                                            }
                                            errorKey={creativesErrorKey}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleCancel}
                                disabled={isLoading || isSubmitting}
                                className="flex-1"
                            >
                                Отмена
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading || isSubmitting}
                                className="flex-1"
                            >
                                {isLoading || isSubmitting
                                    ? 'Сохранение...'
                                    : 'Сохранить'}
                            </Button>
                        </div>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    )
}

const CreateAgencyActDialogWrapper: React.FC<
    Omit<CreateAgencyActDialogProps, 'contract'> & {
        contract: VkOrdAgencyContractResponse | null
    }
> = ({ open, contract, ...props }) => {
    if (!open || !contract) {
        return null
    }

    return <CreateAgencyActDialog {...props} open={open} contract={contract} />
}

export default CreateAgencyActDialogWrapper
